import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { AlertTriangle, CheckCircle2, KeyRound, ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { navigateWithTransition } from "@/lib/viewTransition";
import { AuthLayout } from "@/components/AuthLayout";
import { PasswordInput } from "@/components/ui/PasswordInput";
import { Button } from "@/components/ui/Button";

const FEATURES = [
  { icon: KeyRound, label: "El enlace solo se puede usar una vez" },
  { icon: ShieldCheck, label: "Se cerrará cualquier otra sesión abierta con la contraseña anterior" },
];

const schema = z
  .object({
    password: z.string().min(8, "Mínimo 8 caracteres"),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Las contraseñas no coinciden",
    path: ["confirmPassword"],
  });
type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  async function onSubmit(data: FormValues) {
    if (!token) return;
    setSubmitError(null);
    try {
      await api.post("/auth/reset-password", { token, password: data.password });
      setDone(true);
    } catch (error: any) {
      setSubmitError(error?.response?.data?.error ?? "No se pudo restablecer la contraseña. Inténtalo de nuevo.");
    }
  }

  return (
    <AuthLayout
      headline="Elige una nueva contraseña"
      subtext="En cuanto la guardes podrás volver a entrar en el panel con ella."
      features={FEATURES}
      formEyebrow={
        !done && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700">
            <Sparkles className="h-3.5 w-3.5" />
            Restablecer contraseña
          </span>
        )
      }
    >
      {!token ? (
        <div className="flex flex-col items-start gap-4">
          <span className="auth-in flex h-12 w-12 items-center justify-center rounded-2xl bg-red-50 text-red-600">
            <AlertTriangle className="h-6 w-6" />
          </span>
          <h2 className="auth-in text-2xl font-bold text-ink-900">Enlace incompleto</h2>
          <p className="auth-in text-sm text-ink-500">
            Este enlace no incluye el código necesario para restablecer la contraseña. Solicita uno nuevo desde
            la pantalla de recuperación.
          </p>
          <a
            href="/olvide-mi-contrasena"
            onClick={(e) => {
              e.preventDefault();
              navigateWithTransition(navigate, "/olvide-mi-contrasena");
            }}
            className="text-sm font-medium text-brand-600 hover:text-brand-700"
          >
            Pedir un nuevo enlace
          </a>
        </div>
      ) : done ? (
        <div className="flex flex-col items-start gap-4">
          <span className="auth-in flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
            <CheckCircle2 className="h-6 w-6" />
          </span>
          <h2 className="auth-in text-2xl font-bold text-ink-900" style={{ animationDelay: "60ms" }}>
            Contraseña actualizada
          </h2>
          <p className="auth-in text-sm text-ink-500" style={{ animationDelay: "120ms" }}>
            Ya puedes iniciar sesión con tu nueva contraseña.
          </p>
          <div className="auth-in" style={{ animationDelay: "180ms" }}>
            <a
              href="/login"
              onClick={(e) => {
                e.preventDefault();
                navigateWithTransition(navigate, "/login");
              }}
              className="text-sm font-medium text-brand-600 hover:text-brand-700"
            >
              Ir a iniciar sesión
            </a>
          </div>
        </div>
      ) : (
        <>
          <h2 className="auth-in text-2xl font-bold text-ink-900">Nueva contraseña</h2>
          <p className="auth-in mt-1 mb-8 text-sm text-ink-400" style={{ animationDelay: "60ms" }}>
            Elige una contraseña que no uses en ningún otro sitio.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
            <div className="auth-in" style={{ animationDelay: "120ms" }}>
              <PasswordInput id="password" label="Nueva contraseña" error={errors.password?.message} {...register("password")} />
            </div>
            <div className="auth-in" style={{ animationDelay: "160ms" }}>
              <PasswordInput
                id="confirmPassword"
                label="Confirmar contraseña"
                error={errors.confirmPassword?.message}
                {...register("confirmPassword")}
              />
            </div>

            {submitError && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600">{submitError}</p>}

            <Button type="submit" disabled={isSubmitting} className="auth-in mt-2 w-full" style={{ animationDelay: "200ms" }}>
              {isSubmitting ? "Guardando…" : "Guardar nueva contraseña"}
            </Button>
          </form>
        </>
      )}
    </AuthLayout>
  );
}
